import PersonalService from '../../../services/PersonalService'
import { SET_LOADING, SET_LOADING_EXTENDED, SET_PERSONAL_STATE_VALUE, SET_SELECTED_ACCOUNTS, CLEAR_STATE_DATA } from './variables'
import { BASE_COUNTRY, fileFormats, dateRanges, psuTypes } from '../../../constants'
import Camelize from '../../../services/Camelize'
import { i18n, dateOptions } from '../../../language/i18n'

const state = {
  loading: false,
  loadingExtended: false,
  accounts: [],
  accountsExtended: null,
  selectedCountry: (new URLSearchParams(window.location.search).get('country') ||
    localStorage.getItem('selectedCountry') ||
    BASE_COUNTRY),
  selectedPsuType: (new URLSearchParams(window.location.search).get('usage') ||
    localStorage.getItem('selectedPsuType') ||
    psuTypes.personal),
  selectedAccounts: JSON.parse(localStorage.getItem('selectedAccounts') || '[]'),
  selectedFileFormat: localStorage.getItem('selectedFileFormat') || fileFormats.json,
  selectedDateRangeValue: localStorage.getItem('selectedDateRangeValue') || null,
  selectedDateRangeType: localStorage.getItem('selectedDateRangeType') || dateRanges.Year
}

const mutations = {
  [SET_SELECTED_ACCOUNTS](state, value) {
    const selectedAccounts = new Set(state.selectedAccounts)

    if (selectedAccounts.has(value)) {
      selectedAccounts.delete(value)
    } else {
      selectedAccounts.add(value)
    }
    localStorage.setItem('selectedAccounts', JSON.stringify([...selectedAccounts]))
    state.selectedAccounts = [...selectedAccounts]
  },
  [SET_LOADING]: (state, value) => {
    state.loading = value
  },
  [SET_LOADING_EXTENDED]: (state, value) => {
    state.loadingExtended = value
  },
  [SET_PERSONAL_STATE_VALUE]: (state, value) => {
    state[value[0]] = value[1]
    if (['selectedCountry', 'selectedPsuType'].indexOf(value[0]) !== -1) {
      localStorage.setItem(value[0], value[1])
      history.pushState({}, '', window.location.pathname)
    }
  },
  [CLEAR_STATE_DATA]: state => {
    state.accounts = []
    state.accountsExtended = null
    state.selectedAccounts = ''
    state.selectedFileFormat = fileFormats.pdf
    state.selectedDateRangeValue = null
    state.selectedDateRangeType = dateRanges.Year
  }
}

const getters = {
  selectedAccounts: state => state.selectedAccounts,
  selectedFileFormat: state => state.selectedFileFormat,
  selectedDateRangeValue: state => state.selectedDateRangeValue,
  selectedDateRangeType: state => state.selectedDateRangeType,
  selectedCountry: state => state.selectedCountry,
  selectedPsuType: state => state.selectedPsuType,
  formattedDatesRange: (_state, getters) => {
    const datesArray = getters.arrayOfDatesRange

    if (datesArray && datesArray.length > 1) {
      return `${new Date(datesArray[0]).toLocaleDateString(i18n.locale, dateOptions(i18n.locale))}
      - ${new Date(datesArray[1]).toLocaleDateString(i18n.locale, dateOptions(i18n.locale))}`
    }
    return null
  },
  arrayOfDatesRange: state => {
    if (!state.selectedDateRangeValue) {
      return null
    }
    const datesArray = state.selectedDateRangeValue.split(' to ')
    return datesArray
  },
  selectedAccountsData: getters => {
    return getters.selectedAccounts.map(id => getters.accounts.find(account => account.resourceId === id))
  },
  accountNameByResourceId: getters => (id) => getters.accounts.find(account => account.resourceId === id).name,
  accounts: state => state.accounts,
  accountsExtended: state => state.accountsExtended
}

const actions = {
  wipeAllData({ commit }) {
    const preservedItems = {
      cookieAccepted: null,
      darkMode: null,
      selectedCountry: null,
      selectedPsuType: null
    }
    for (const k of Object.keys(preservedItems)) {
      preservedItems[k] = localStorage.getItem(k)
    }
    commit(CLEAR_STATE_DATA)
    localStorage.clear()
    for (const [k, v] of Object.entries(preservedItems)) {
      if (v !== null) {
        localStorage.setItem(k, v)
      }
    }
  },
  async accountsRequest({ commit, dispatch }) {
    commit(SET_LOADING, true)

    try {
      const response = await PersonalService.getAccounts()
      commit(SET_PERSONAL_STATE_VALUE, ['accounts', Camelize.accountsFromDto(response.data.response.accounts)])
    } catch (error) {
      dispatch('base/pushAlertMessage', { message: error }, { root: true })
      return false
    } finally {
      commit(SET_LOADING, false)
    }
  },
  async accountsExtendedRequest({ commit, dispatch }) {
    commit(SET_LOADING_EXTENDED, true)

    try {
      const accounts = await PersonalService.getAccountsExtended()
      const response = accounts.data.response

      if (!response || response === null) {
        return false
      }
      commit(SET_PERSONAL_STATE_VALUE, ['accountsExtended', Camelize.accountsExtendedFromDto(accounts.data.response)])
      return true
    } catch (error) {
      dispatch('base/pushAlertMessage', { message: error }, { root: true })
      return false
    } finally {
      commit(SET_LOADING_EXTENDED, false)
    }
  },
  async exportDataRequest({ dispatch, commit, getters }) {
    commit(SET_LOADING, true)
    try {
      const payload = {
        accounts: getters.selectedAccounts,
        date_from: getters.arrayOfDatesRange[0],
        date_to: getters.arrayOfDatesRange[1],
        export_format: getters.selectedFileFormat
      }
      const response = await PersonalService.exportData(payload)

      if (response && response.data.response) {
        return true
      }
      throw response.error
    } catch (error) {
      const errorMessage = (error.response && error.response.data.detail.message) || error
      dispatch('base/pushAlertMessage', { message: errorMessage }, { root: true })
    } finally {
      commit(SET_LOADING, false)
    }
  },
  async exportStatusRequest({ commit, dispatch }) {
    commit(SET_LOADING, true)
    try {
      const response = await PersonalService.getExportStatus()
      const responseData = response && response.data.response

      return responseData
    } catch (error) {
      dispatch('base/pushAlertMessage', { message: error }, { root: true })
    } finally {
      commit(SET_LOADING, false)
    }
  },
  setAccountSelected({ commit }, value) {
    commit(SET_SELECTED_ACCOUNTS, value)
  }
}

export default {
  namespaced: true,

  state,
  getters,
  actions,
  mutations
}
